"use client";

/**
 * Dashboard-scope error boundary — catches runtime errors so users see a
 * friendly message + retry button instead of a blank "client-side exception"
 * screen.
 */
import { useEffect } from "react";
import Link from "next/link";

export default function DashboardErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Surface in browser console for diagnosis
    console.error("[DashboardErrorBoundary]", error);
  }, [error]);

  return (
    <main className="min-h-[60vh] flex items-center justify-center px-4 py-10">
      <div className="max-w-lg w-full rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
        <div className="text-5xl mb-2">⚠️</div>
        <h1 className="text-xl font-bold text-red-800">เกิดข้อผิดพลาดภายในหน้าเว็บ</h1>
        <p className="mt-2 text-sm text-slate-700">
          ระบบพบ exception ขณะ render หน้านี้ ลองรีเฟรช หรือกลับไปหน้า Dashboard
        </p>
        {error?.message && (
          <div className="mt-3 rounded bg-white border border-red-200 p-2 text-left text-xs text-red-700 font-mono break-all">
            {error.message}
            {error.digest && <div className="mt-1 text-[10px] text-slate-500">digest: {error.digest}</div>}
          </div>
        )}
        <div className="mt-4 flex justify-center gap-2 flex-wrap">
          <button
            onClick={() => reset()}
            className="rounded-md bg-red-500 hover:bg-red-600 text-white text-sm px-4 py-2"
          >
            🔄 ลองใหม่
          </button>
          <Link
            href="/dashboard"
            className="rounded-md border border-slate-300 hover:bg-slate-50 text-sm px-4 py-2"
          >
            ← กลับ Dashboard
          </Link>
        </div>
        <div className="mt-4 text-[11px] text-slate-500">
          ถ้ายังพบ error ซ้ำๆ — เปิด DevTools (F12) → Console → copy ข้อความสีแดง ส่งให้ทีมพัฒนา
        </div>
      </div>
    </main>
  );
}
